import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '@/lib/api'

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time.
 *
 * TanStack Query hooks capture their closure at render; a screen whose `me`
 * query resolves after first render would otherwise send the mutation with an
 * empty X-Company-Id. The ref holds the latest companyId between renders.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

export type TaxRate = components['schemas']['TaxRateResource']
export type Settings = components['schemas']['AccountingSettingsResource']

export function useSettings(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Settings | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Settings | null, Error>({
    queryKey: ['companies', companyId, 'accounting', 'settings'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Settings | null> => {
      if (!companyId) return null
      const response = await api.GET('/accounting/settings', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load settings')
      return unwrap<Settings>(response.data ?? {}, 'settings')
    },
  })
}

export interface SettingsUpdateBody {
  tax_regime?: string
  einvoice_adapter?: string
  rounding_mode?: string
  discount_before_tax?: boolean
  presentation_decimals?: number
  fx_revaluation_enabled?: boolean
  wht_enabled?: boolean
  opening_balance_date?: string | null
}

export function useUpdateSettings(
  companyId: string | null,
  options?: Omit<UseMutationOptions<Settings, Error, SettingsUpdateBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Settings, Error, SettingsUpdateBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.PATCH('/accounting/settings', {
        headers: { 'X-Company-Id': currentCompany() },
        body: body as components['schemas']['UpdateAccountingSettingsRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to save settings')
      return unwrap<Settings>(response.data ?? {}, 'settings')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'settings'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export interface TaxRateBody {
  code: string
  name: Record<string, string>
  tax_type: string
  /** A percentage — 14 is 14%, as `TaxEngine` reads it. */
  rate: string
  scope: string
  /** False keeps a schedule rate's amount out of the VAT base (ETA non-taxable fees). */
  in_vat_base?: boolean
  effective_from: string
  effective_to?: string | null
  is_active?: boolean
}

/**
 * Create a tax rate (FR-ACC-070, 073).
 *
 * The screen listed three rates and offered nothing else, though `POST` and
 * `PATCH /accounting/tax-rates` have existed since Wave 1 — the same
 * defect class as the chart of accounts before it grew its form.
 */
export function useCreateTaxRate(
  companyId: string | null,
  options?: Omit<UseMutationOptions<TaxRate, Error, TaxRateBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<TaxRate, Error, TaxRateBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/tax-rates', {
        headers: { 'X-Company-Id': currentCompany() },
        // Schema artifact: `name: string[]` vs `{ en, ar }` (see account store).
        body: body as unknown as components['schemas']['TaxRateStoreRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create tax rate')
      return unwrap<TaxRate>(response.data ?? {}, 'tax-rates')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Amend a tax rate.
 *
 * Rates are effective-dated (spec D7), so changing a live rate's percentage is
 * usually wrong — the correct move is to close the current one with an
 * `effective_to` and add its successor. The dialog says so; the API allows
 * either, because a typo in a rate that has never been used on a document is a
 * real case.
 */
export function useUpdateTaxRate(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<TaxRate, Error, { id: string; body: Partial<TaxRateBody> }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<TaxRate, Error, { id: string; body: Partial<TaxRateBody> }>({
    ...options,
    mutationFn: async ({ id, body }) => {
      const response = await api.PATCH('/accounting/tax-rates/{rate}', {
        params: { path: { rate: id } },
        headers: { 'X-Company-Id': currentCompany() },
        // Schema artifact: `name: string[]` vs `{ en, ar }`.
        body: body as unknown as components['schemas']['TaxRateStoreRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to update tax rate')
      return unwrap<TaxRate>(response.data ?? {}, 'tax-rates')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}
